import React, {useEffect, useMemo, useRef, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useStores} from '../../store';
import {useLocation, useParams} from 'react-router-dom';
import usePromiseFeedback from '../../shared/promise-feedback/usePromiseFeedback';
import AppHeader from '../app-header/app-header';
import {INTERVAL_POLLING} from '@src/utils/constants';
import isNumeric from 'antd/es/_util/isNumeric';
import './ecm.scss';
import EcmFilters from './ecm-filters/ecm-filters';
import {ConnectionModel} from './connection.model';
import {OnboardingModals} from '../onboarding-modals/onboarding-modals';
import InitialAlert from './initial-alert/initial-alert';
import ConnectionTable from './ecm-table/ecm-table';
import {MainPages, PolicyMode, VersionLabel} from '@src/utils/enums';
import PolicyActionToast from './ecm-table/policy-actions-modal/policy-action-toast';
import MlRecommendationModal from '../ml-recommendation-modal/ml-recommendation-modal';
import {usePageViewEvent} from '@src/components/shared/hooks/use-page-view-event';
import {EmailLinkSource} from '@src/dtos/analytics.dto';

const Ecm: React.FC = observer(() => {
    const [showMlModal, setShowMlModal] = useState(false);

    const { customerStore,
        userStore,
        userPreferencesStore,
        notificationStore,
        fwVersionStore,
        uiStore } = useStores();
    const [filteredConnections, setFilteredConnections] = useState<ConnectionModel[] | undefined>(undefined);
    const [selectedConnections, setSelectedConnections] = useState<Map<number, ConnectionModel>>(new Map());
    const [promiseFeedbackFactory] = usePromiseFeedback();
    const { customerId } = useParams<{ customerId: string }>();
    const intervalRef = useRef<number | null>(null);
    const { state } = useLocation();
    const { isWizardComplete, wizardConnectionId } = state ?? {};
    const [gotItPopoverConnectionId, setGotItPopoverConnectionId] = useState<number | null>(null);

    const { toastData } = customerStore;

    usePageViewEvent(MainPages.CONNECTIONS, undefined, false);

    useEffect(() => {
        uiStore.setMainPage(MainPages.CONNECTIONS);
        return () => {
            uiStore.setEmailLinkSource(EmailLinkSource.NONE);
        };
    }, []);

    useEffect(() => {
        if (!customerStore.isLoading && !customerStore.data) {
            promiseFeedbackFactory({
                promise: customerStore.load(),
                notifObj: { show: false }
            });
        }
        return () => {
            customerStore.resetConnectionsData();
        };
    }, []);


    useEffect(() => {
        if (customerStore.data && isNumeric(customerId)) {
            void customerStore.setCustomerById(Number(customerId));
            void loadConnections();
            void userPreferencesStore.load(true);
            void notificationStore.loadWhatsNewLastSeen();
            intervalRef.current ??= window.setInterval(loadConnections, INTERVAL_POLLING);
            return () => {
                if (intervalRef.current !== null) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            };
        }
    }, [customerStore.data, customerId]);

    useEffect(() => {
        const mlData = fwVersionStore.mlRecommendationData;
        if (
            mlData &&
            ((mlData.applicationsToBlock && mlData.applicationsToBlock.length > 0) ||
                (mlData.ipsToBlock && mlData.ipsToBlock.length > 0))
        ) {
            setShowMlModal(true);
        } else {
            setShowMlModal(false);
        }
    }, [fwVersionStore.mlRecommendationData]);

    const loadConnections = async () => {
        await customerStore.loadConnectionsData(Number(customerId));
        await notificationStore.loadNotifications(true);
    };

    const initialConnectionCount = useMemo(() =>
        customerStore.connectionList?.filter(conn => conn.policyStatus === VersionLabel.Initial).length || 0,
        [customerStore.connectionList]);

    useEffect(() => {
        if (isWizardComplete && wizardConnectionId === customerStore.serviceId && customerStore.policyMode === PolicyMode.CUSTOM_PROTECTION) {
            setGotItPopoverConnectionId(wizardConnectionId);
        }
    }, [isWizardComplete, wizardConnectionId, customerStore.serviceId, customerStore.policyMode]);

    const serviceHasDraft = (serviceId: number) => {
        const service = customerStore.connectionList?.find(conn => conn.id === serviceId);
        return service?.policyStatus === VersionLabel.Draft;
    };

    return (<>
        {showMlModal && (
            <MlRecommendationModal
                onClose={() => {
                    setShowMlModal(false);
                    fwVersionStore.setMlRecommendationData(undefined);
                }}
                hasDraft={serviceHasDraft(fwVersionStore.mlRecommendationData?.serviceId ?? 0)}
            />
        )}
        <AppHeader isECM={true} />
        <div className="ecm">
            {customerStore.connectionList && !userStore.isReadOnlyUser && isWizardComplete && wizardConnectionId === customerStore.serviceId &&
                <OnboardingModals ignoreDraftToSmartPolicy={true} shouldLoadVersions={true} />}
            <InitialAlert connectionsCount={initialConnectionCount} />
            <EcmFilters setData={setFilteredConnections}
                setSelectedConnections={setSelectedConnections} />
            <ConnectionTable
                connections={filteredConnections}
                selectedConnections={selectedConnections}
                setSelectedConnections={setSelectedConnections}
                gotItPopoverConnectionId={gotItPopoverConnectionId}
                onCloseGotItPopover={() => setGotItPopoverConnectionId(null)}
            />
        </div>
        {toastData && (
            <PolicyActionToast
                title={toastData.title}
                description={toastData.desc}
                onClose={() => customerStore.setToastData(null)}
            />
        )}
    </>);
});

export default Ecm;
